import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import i18n from 'i18next';
import { loadKakaoSDK } from './index';
import { isInKorea } from 'common/utils/regionDetector';
import { addPlaceHistory, matchPlaceHistory, PlaceHistoryItem } from 'common/utils/searchHistory';
import { isNativeApp, openNativeGooglePlaceSearch, openNativePlaceExplore } from 'common/webview';
import { useUserLocation } from 'hooks/useUserLocation';
import { myLocationHtml } from './markerStyle';
import { LeafletPlacePicker } from './LeafletPlacePicker';
import { placeMarkerHtml } from './markerStyle';
import { IcClose, IcPin, IcPushpin, IcStar } from 'components/common/icons/EmojiIcons';
import { CategoryIcon } from 'components/common/icons/categoryIcon';
import config from 'config/config';
import styles from './PlaceMapView.module.css';
import { haversineMeters } from 'common/utils/geo';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface MapPlace {
    id: string;
    name: string;
    address: string;
    category?: string;
    categories?: string[];
    categoryIcon?: string;
    categoryLabel?: string;
    lat: number;
    lng: number;
    imageUrl?: string;
    rating?: number;
    reviewCount?: number;
    openingHours?: string;
    phone?: string;
    /** 제공자 원본 장소 id (예: 'kakao-12089662') — 사진 조회 키.
     *  id 필드는 화면에서 타입을 덧붙여 쓰므로 쪼개 복원하면 안 됩니다. */
    placeId?: string;
    isSearchResult?: boolean; // 검색 결과인지 추천 장소인지 구분
    distance?: string; // 이전 장소 기준 거리 라벨 (예: "1.2km", "350m") — sub_place 추가 흐름 시 표시
}

interface PlaceMapViewProps {
    places: MapPlace[];
    center?: { lat: number; lng: number };
    selectedPlaceId?: string | null;
    categories?: Array<{ id: string; label: string; icon: string }>;
    selectedCategory?: string;
    onCategoryChange?: (categoryId: string) => void;
    onPlaceSelect?: (place: MapPlace) => void;
    onPlaceConfirm?: (place: MapPlace) => void;
    /**
     * 지도 중심이 사용자 의도로 변경됐을 때 호출 — 부모가 currentLocation 갱신해
     * 새 좌표 주변 추천 장소를 재로딩하도록.
     * (a) 지도 패닝/줌 후 '여기서 검색' 버튼 클릭
     * (b) 검색 결과 항목 클릭 시 자동
     */
    onCenterChange?: (center: { lat: number; lng: number }) => void;
    onClose?: () => void;
    onMapClick?: () => void;
    detailOpen?: boolean;
    onVisiblePlacesChange?: (visiblePlaceIds: string[]) => void;
    showCategories?: boolean;
    showBackButton?: boolean;
    showSearch?: boolean;
    confirmButtonText?: string;
    height?: string;
    /** 검색 결과 항목과 카드에 표시할 거리 기준 좌표 (예: 이전 sub_place 좌표). 있으면 "이전 장소에서 X.Xkm" 형식 라벨 표시 */
    distanceReferenceCenter?: { lat: number; lng: number } | null;
    /** 거리 라벨 prefix (기본: "이전 장소에서") */
    distanceReferenceLabel?: string;
    /** rev4 · 마커 스타일 mode — Leaflet 경로에만 적용 (해외 좌표)
     *  - simple: 원형 카테고리 pin (기본)
     *  - attraction: 아이콘+별점 chip 마커
     *  - hotel: 가격 라벨 마커 (없으면 아이콘 fallback) */
    markerMode?: 'simple' | 'attraction' | 'hotel';
    /** 해외+앱에서 검색창을 누르면 열리는 네이티브 검색 화면의 제목 */
    nativeSearchTitle?: string;
}

export const PlaceMapView: React.FC<PlaceMapViewProps> = ({
    places,
    center,
    selectedPlaceId,
    categories,
    selectedCategory = 'all',
    onCategoryChange,
    onPlaceSelect,
    onPlaceConfirm,
    onCenterChange,
    onClose,
    onMapClick,
    detailOpen: detailOpenProp,
    onVisiblePlacesChange,
    showCategories = true,
    showBackButton = true,
    showSearch = true,
    confirmButtonText = '이 장소로 선택',
    height = 'calc(100vh - 56px)',
    distanceReferenceCenter,
    distanceReferenceLabel = '이전 장소에서',
    markerMode = 'simple',
    nativeSearchTitle,
}) => {
    const { location: cachedUserLocation, fetchLocation: fetchUserLocation } = useUserLocation();
    const [isFetchingMyLoc, setIsFetchingMyLoc] = useState(false);
    /** 지도에 점으로 찍을 내 위치. 버튼은 지도를 옮길 뿐이라, 한 번 움직이면
     *  내가 어디 있는지 화면에서 사라졌습니다. */
    const [myLoc, setMyLoc] = useState<{ lat: number; lng: number } | null>(null);
    const myLocOverlayRef = useRef<any>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<kakao.maps.Map | null>(null);
    const markersRef = useRef<kakao.maps.Marker[]>([]);
    const overlaysRef = useRef<kakao.maps.CustomOverlay[]>([]);
    const placesServiceRef = useRef<kakao.maps.services.Places | null>(null);
    const geocoderRef = useRef<kakao.maps.services.Geocoder | null>(null);
    const cardScrollRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
    const onMapClickRef = useRef(onMapClick);
    const onPlaceSelectRef = useRef(onPlaceSelect);
    onPlaceSelectRef.current = onPlaceSelect;
    const onCenterChangeRef = useRef(onCenterChange);
    /* 검색이 읽는 지도 중심. deps 가 아니라 ref 로 두는 이유는 아래 검색 effect 주석 참고 */
    const centerRef = useRef(center);
    onCenterChangeRef.current = onCenterChange;
    centerRef.current = center;

    // 즐겨찾는 지역 — localStorage 저장 (디바이스별)
    interface FavoritePlace { name: string; lat: number; lng: number; }
    const FAVORITES_KEY = 'pmv_place_favorites';
    const readFavorites = (): FavoritePlace[] => {
        try {
            const raw = localStorage.getItem(FAVORITES_KEY);
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr) ? arr.filter(f => f?.name && typeof f.lat === 'number' && typeof f.lng === 'number') : [];
        } catch { return []; }
    };
    const writeFavorites = (list: FavoritePlace[]) => {
        try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(list)); } catch {}
    };
    const [favorites, setFavorites] = useState<FavoritePlace[]>(readFavorites());
    const [showAddFav, setShowAddFav] = useState(false);
    const [favNameInput, setFavNameInput] = useState('');
    const [editFavMode, setEditFavMode] = useState(false);

    const handleAddFavorite = () => {
        const map = mapRef.current;
        if (!map) return;
        const c = map.getCenter();
        const newFav: FavoritePlace = {
            name: favNameInput.trim() || `${c.getLat().toFixed(3)}, ${c.getLng().toFixed(3)}`,
            lat: c.getLat(),
            lng: c.getLng(),
        };
        // 중복 이름 방지 — 같은 이름이면 좌표만 갱신
        const existing = favorites.findIndex(f => f.name === newFav.name);
        const next = existing >= 0
            ? favorites.map((f, i) => i === existing ? newFav : f)
            : [...favorites, newFav];
        setFavorites(next);
        writeFavorites(next);
        setShowAddFav(false);
        setFavNameInput('');
    };

    const handleRemoveFavorite = (name: string) => {
        const next = favorites.filter(f => f.name !== name);
        setFavorites(next);
        writeFavorites(next);
        if (next.length === 0) setEditFavMode(false);
    };

    const handleFavoriteClick = (fav: FavoritePlace) => {
        const newCenter = { lat: fav.lat, lng: fav.lng };
        if (useKakao && mapRef.current) {
            mapRef.current.setCenter(new kakao.maps.LatLng(fav.lat, fav.lng));
        }
        lastQueryCenterRef.current = newCenter;
        setSearchHerePending(null);
        onCenterChangeRef.current?.(newCenter);
    };

    // '여기서 검색' 버튼 — 사용자가 지도 패닝/줌 후 화면 중심이 검색 기준 위치에서 멀어지면 노출
    const [searchHerePending, setSearchHerePending] = useState<{ lat: number; lng: number } | null>(null);
    const lastQueryCenterRef = useRef<{ lat: number; lng: number } | null>(null);

    // 두 좌표 사이 미터 거리 (Haversine 간이 — 200m 임계값 정도면 충분히 정확)
    const distanceMeters = (a: { lat: number; lng: number }, b: { lat: number; lng: number }): number =>
        haversineMeters(a.lat, a.lng, b.lat, b.lng);

    /* 지도가 멈출 때마다 — 화면 중심이 마지막 검색 기준점에서 200m 이상
       멀어지면 '여기서 검색' 을 띄웁니다. 카카오와 구글이 같은 규칙을 씁니다.
       (예전에는 카카오는 여기, 구글은 GoogleMap 안에서 제각각 판단하고
        버튼도 따로 그려서 해외에서만 옛 스타일 알약이 보였습니다.) */
    const handleIdleCenter = useCallback((cur: { lat: number; lng: number }) => {
        const ref = lastQueryCenterRef.current;
        if (!ref) return;
        setSearchHerePending(haversineMeters(cur.lat, cur.lng, ref.lat, ref.lng) > 200 ? cur : null);
    }, []);
    // 지도 리스너는 한 번만 달므로 최신 콜백을 ref 로 건네줍니다.
    const handleIdleCenterRef = useRef(handleIdleCenter);
    handleIdleCenterRef.current = handleIdleCenter;

    const [sdkLoaded, setSdkLoaded] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState<MapPlace | null>(null);
    const [customPlace, setCustomPlace] = useState<MapPlace | null>(null);
    /* 카카오 지도를 새로 만들 때마다 올립니다. provider 가 도중에 바뀌면
       지도 객체가 나중에 생기는데, 마커 effect 가 그때 다시 돌지 않으면
       지도만 있고 마커가 없는 상태로 남습니다. */
    const [kakaoMapEpoch, setKakaoMapEpoch] = useState(0);

    // 한국/해외 자동 판별 (center 좌표 기반)
    const useKakao = useMemo(() => {
        if (!center) return true; // 기본: 카카오맵
        return isInKorea(center.lat, center.lng);
    }, [center]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<MapPlace[]>([]);
    const [searchDropdownResults, setSearchDropdownResults] = useState<MapPlace[]>([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    /* 결과를 고른 직후 한 번은 검색 이펙트를 건너뜁니다.
       고르면 searchQuery 가 그 장소 이름으로 바뀌고 center 도 옮겨져
       이펙트가 한 번 더 돈다는 게 문제였습니다. 그때 드롭다운이
       도로 열려 지도를 덮므로, 고른 장소가 지도에 안 보이고
       아무 반응도 없는 것처럼 보였습니다. */
    const skipNextSearchRef = useRef(false);
    const [historyMatches, setHistoryMatches] = useState<PlaceHistoryItem[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // 표시할 장소: 커스텀 장소 + 검색 결과 + 추천 장소
    const displayPlaces = [
        ...(customPlace ? [customPlace] : []),
        ...(searchResults.length > 0 ? searchResults : []),
        ...places.filter(p =>
            !searchResults.find(s => s.name === p.name) &&
            customPlace?.id !== p.id
        ),
    ];

    onMapClickRef.current = onMapClick;

    // 카카오 SDK 로드 (한국일 때만)
    useEffect(() => {
        if (useKakao) {
            loadKakaoSDK().then(() => setSdkLoaded(true)).catch(() => {});
        }
    }, [useKakao]);

    // 외부에서 선택된 장소 동기화
    useEffect(() => {
        if (selectedPlaceId) {
            const found = displayPlaces.find(p => p.id === selectedPlaceId);
            if (found) setSelectedPlace(found);
        }
    }, [selectedPlaceId]);

    /* 카카오맵 초기화 (한국일 때만)
     *
     * useKakao 가 deps 에 있어야 합니다. provider 는 center 로 정해지므로
     * 해외 지도에서 국내 장소를 고르면 도중에 뒤집히는데, 그때 이 effect 가
     * 돌지 않으면 카카오용 div 만 빈 칸으로 남습니다 — 파리 여행에서 집결지로
     * 인천공항을 골라도 지도가 그대로였던 이유입니다. 정리 함수도 그동안
     * 한 번도 불리지 않아 mapRef 가 떼어낸 지도를 붙들고 있었습니다. */
    useEffect(() => {
        if (!useKakao || !sdkLoaded || !mapContainerRef.current) return;

        const defaultCenter = center || { lat: 37.5665, lng: 126.978 };
        const map = new kakao.maps.Map(mapContainerRef.current, {
            center: new kakao.maps.LatLng(defaultCenter.lat, defaultCenter.lng),
            level: 4,
        });
        mapRef.current = map;
        placesServiceRef.current = new kakao.maps.services.Places();
        geocoderRef.current = new kakao.maps.services.Geocoder();
        lastQueryCenterRef.current = defaultCenter;

        // '여기서 검색' 트리거 — 지도 idle 이벤트로 화면 중심이 마지막 검색 기준점에서
        // 200m 이상 멀어지면 버튼 노출
        kakao.maps.event.addListener(map, 'idle', () => {
            const c = map.getCenter();
            handleIdleCenterRef.current({ lat: c.getLat(), lng: c.getLng() });
        });

        // 지도 클릭 → 임의 좌표 선택 (옵션 A): reverse geocode 로 도로명/지번 주소 가져와 customPlace 생성.
        // 추천 장소 외에 사용자가 지도의 임의 지점을 직접 선택할 수 있도록 함.
        kakao.maps.event.addListener(map, 'click', (e: any) => {
            const latLng = e?.latLng;
            if (!latLng) {
                onMapClickRef.current?.();
                return;
            }
            const lat = latLng.getLat();
            const lng = latLng.getLng();
            const geocoder = geocoderRef.current;
            if (!geocoder) return;
            geocoder.coord2Address(lng, lat, (result: any, status: any) => {
                if (status !== 'OK') {
                    // 주소 조회 실패해도 좌표만으로 선택 가능
                    const place: MapPlace = {
                        id: `custom-${Date.now()}`,
                        name: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                        address: '',
                        lat, lng,
                        categoryIcon: 'pin',
                        categoryLabel: i18n.t('placeMap.directSelect'),
                        isSearchResult: false,
                    };
                    setCustomPlace(place);
                    setSelectedPlace(place);
                    onPlaceSelectRef.current?.(place);
                    return;
                }
                const r = result?.[0] || {};
                const roadAddr = r.road_address?.address_name || '';
                const jibunAddr = r.address?.address_name || '';
                const name = roadAddr || jibunAddr || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                const address = roadAddr || jibunAddr || '';
                const place: MapPlace = {
                    id: `custom-${Date.now()}`,
                    name,
                    address,
                    lat, lng,
                    categoryIcon: 'pin',
                    categoryLabel: i18n.t('placeMap.directSelect'),
                    isSearchResult: false,
                };
                setCustomPlace(place);
                setSelectedPlace(place);
                onPlaceSelectRef.current?.(place);
            });
        });

        setKakaoMapEpoch(e => e + 1);

        return () => { mapRef.current = null; };
    }, [sdkLoaded, useKakao]); // eslint-disable-line react-hooks/exhaustive-deps

    // 검색 (디바운스) → 드롭다운에 결과 표시
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (skipNextSearchRef.current) {
            skipNextSearchRef.current = false;
            return;
        }

        const query = searchQuery.trim();
        if (!query || query.length < 2) {
            setSearchDropdownResults([]);
            // history 매칭이 있으면 dropdown 유지 (최근 검색 표시), 없으면 닫기.
            const hasHistory = matchPlaceHistory(searchQuery, 5).length > 0;
            setShowSearchDropdown(hasHistory);
            return;
        }
        // 2글자 이상 — dropdown 즉시 노출 (검색 중 표시), 디바운스 후 결과 갱신
        setShowSearchDropdown(true);
        if (useKakao && !placesServiceRef.current) {
            if (sdkLoaded && window.kakao?.maps?.services) {
                placesServiceRef.current = new kakao.maps.services.Places();
            } else {
                // eslint-disable-next-line no-console
                console.warn('[PMV-search] Kakao SDK not ready', { sdkLoaded, hasKakao: !!window.kakao, hasServices: !!window.kakao?.maps?.services });
                setSearchDropdownResults([]);
                return;
            }
        }

        debounceRef.current = setTimeout(async () => {
            setIsSearching(true);

            if (useKakao && placesServiceRef.current) {
                // 한국: 카카오 Places API
                const biasCenter = centerRef.current;
                const searchCenter = biasCenter
                    ? new kakao.maps.LatLng(biasCenter.lat, biasCenter.lng)
                    : undefined;

                /* 이름으로 못 찾으면 주소로 한 번 더 — keywordSearch 는 장소 이름 색인이라
                   "인천광역시 중구 공항로 271" 같은 주소를 넣으면 ZERO_RESULT 로 끝납니다.
                   그러면 드롭다운이 비어 고를 것이 없고, 지도는 그대로 있어
                   「주소를 검색했는데 마커가 안 찍힌다」 가 됩니다. */
                const searchByAddress = () => {
                    const geocoder = geocoderRef.current;
                    if (!geocoder) {
                        setSearchDropdownResults([]);
                        setShowSearchDropdown(true);
                        setIsSearching(false);
                        return;
                    }
                    geocoder.addressSearch(query, (addrResult: any, addrStatus: any) => {
                        if (addrStatus === 'OK' && addrResult?.length > 0) {
                            setSearchDropdownResults(addrResult.map((r: any, idx: number) => {
                                const road = r.road_address;
                                const addr = road?.address_name || r.address?.address_name || r.address_name || '';
                                return {
                                    /* 주소는 장소 id 가 없어 좌표로 키를 만듭니다 */
                                    id: `addr-${r.y},${r.x}-${idx}`,
                                    name: road?.building_name || addr || query,
                                    address: addr,
                                    category: '',
                                    lat: parseFloat(r.y),
                                    lng: parseFloat(r.x),
                                    categoryIcon: 'search',
                                    isSearchResult: true,
                                };
                            }));
                        } else {
                            setSearchDropdownResults([]);
                        }
                        setShowSearchDropdown(true);
                        setIsSearching(false);
                    });
                };

                placesServiceRef.current.keywordSearch(
                    query,
                    (result, status) => {
                        if (status === 'OK' && result.length > 0) {
                            const mapped = result.map((r, idx) => ({
                                id: r.id || `search-${idx}`,
                                name: r.place_name,
                                address: r.road_address_name || r.address_name,
                                category: r.category_name || '',
                                lat: parseFloat(r.y),
                                lng: parseFloat(r.x),
                                categoryIcon: 'search',
                                isSearchResult: true,
                            }));
                            setSearchDropdownResults(mapped);
                            setShowSearchDropdown(true);
                            setIsSearching(false);
                        } else {
                            searchByAddress();
                        }
                    },
                    { location: searchCenter, size: 10 }
                );
            } else {
                // 해외: 한국어 사용자 → Google Places, 그 외 → Nominatim
                const userLang = navigator.language || '';
                const googleApiKey = config.GOOGLE_MAPS_API_KEY;
                const useGoogle = userLang.startsWith('ko') && googleApiKey;

                try {
                    let mapped: MapPlace[] = [];

                    if (useGoogle) {
                        // Google Places Text Search (New) API
                        const biasCenter = centerRef.current;
                        const locationBias = biasCenter
                            ? `&locationBias=circle:5000@${biasCenter.lat},${biasCenter.lng}`
                            : '';
                        const res = await fetch(
                            `https://places.googleapis.com/v1/places:searchText`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'X-Goog-Api-Key': googleApiKey,
                                    'X-Goog-FieldMask': 'places.id,places.displayName,places.formattedAddress,places.location,places.types',
                                },
                                body: JSON.stringify({
                                    textQuery: query,
                                    languageCode: 'ko',
                                    maxResultCount: 10,
                                    // locationBias 없이 글로벌 검색 (한국어 결과 반환)
                                }),
                            }
                        );
                        const data = await res.json();
                        if (data?.places?.length > 0) {
                            mapped = data.places.map((p: any, idx: number) => ({
                                id: `gp-${p.id || idx}`,
                                name: p.displayName?.text || '',
                                address: p.formattedAddress || '',
                                category: (p.types || [])[0] || '',
                                lat: p.location?.latitude || 0,
                                lng: p.location?.longitude || 0,
                                categoryIcon: 'search',
                                isSearchResult: true,
                            }));
                        }
                    } else {
                        // Nominatim (비한국어 사용자 or Google키 없음)
                        const bc = centerRef.current;
                        const viewbox = bc
                            ? `&viewbox=${bc.lng - 0.5},${bc.lat - 0.5},${bc.lng + 0.5},${bc.lat + 0.5}&bounded=0`
                            : '';
                        const lang = userLang.split('-')[0] || 'en';
                        const res = await fetch(
                            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=10${viewbox}&accept-language=${lang}`,
                            { headers: { 'User-Agent': 'DOKOCREW-App/1.0' } }
                        );
                        const data = await res.json();
                        if (data?.length > 0) {
                            mapped = data.map((r: any, idx: number) => ({
                                id: `nom-${r.place_id || idx}`,
                                name: r.display_name?.split(',')[0] || r.name || '',
                                address: r.display_name || '',
                                category: r.type || '',
                                lat: parseFloat(r.lat),
                                lng: parseFloat(r.lon),
                                categoryIcon: 'search',
                                isSearchResult: true,
                            }));
                        }
                    }

                    setSearchDropdownResults(mapped);
                    setShowSearchDropdown(true);
                } catch {
                    setSearchDropdownResults([]);
                    setShowSearchDropdown(true);
                }
                setIsSearching(false);
            }
        }, 500);

        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    /* center 를 deps 에 넣지 않습니다.
       provider 편향(카카오 location · 구글 locationBias · Nominatim viewbox)에는 최신 중심이
       필요해서 ref 로 읽되, deps 에 넣으면 지도가 움직일 때마다 이 effect 가 돌아
       입력을 건드리지 않았는데도 setShowSearchDropdown(true) 로 목록이 다시 열렸습니다.
       인천공항을 고른 뒤 지도를 누르거나 추천 카드를 누르면 검색 목록이 되살아나던 원인입니다
       (선택 직후 한 번만 막는 skipNextSearchRef 로는 그 뒤 클릭을 막지 못했습니다).
       검색은 입력이 바뀔 때만 돕니다. 지역을 옮긴 뒤 다시 찾는 것은 '여기서 검색' 이 맡습니다. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, sdkLoaded]);

    // 드롭다운 항목 선택
    const handleSearchItemSelect = (place: MapPlace) => {
        // 검색 결과 선택 → 검색 기록 저장 (모든 지도에서 공유)
        addPlaceHistory({
            name: place.name,
            address: place.address,
            lat: place.lat,
            lng: place.lng,
        });
        setSearchResults([place]);
        setSelectedPlace(place);
        onPlaceSelect?.(place);
        setShowSearchDropdown(false);
        skipNextSearchRef.current = true;
        setSearchQuery(place.name);

        // 지도 이동 (카카오 or Leaflet)
        if (useKakao && mapRef.current) {
            mapRef.current.setCenter(new kakao.maps.LatLng(place.lat, place.lng));
            mapRef.current.setLevel(3);
        }
        // Leaflet은 center prop으로 자동 이동됨

        // 부모에게 중심 변경 알림 → 새 좌표 주변 추천 장소 재로딩
        const newCenter = { lat: place.lat, lng: place.lng };
        lastQueryCenterRef.current = newCenter;
        setSearchHerePending(null);
        onCenterChangeRef.current?.(newCenter);
    };

    // 마커 업데이트 — places/selectedPlace 변경 시 실행
    const clearMarkers = () => {
        markersRef.current.forEach(m => m.setMap(null));
        overlaysRef.current.forEach(o => o.setMap(null));
        markersRef.current = [];
        overlaysRef.current = [];
    };

    // places 키 (중복 렌더 방지)
    const placesKey = displayPlaces.map(p => p.id).join(',');

    useEffect(() => {
        if (!useKakao || !mapRef.current) return;
        const map = mapRef.current;

        clearMarkers();

        displayPlaces.forEach(place => {
            if (!place.lat || !place.lng) return;
            const pos = new kakao.maps.LatLng(place.lat, place.lng);
            const isSelected = selectedPlace?.id === place.id;

            const el = document.createElement('div');
            el.style.cursor = 'pointer';
            el.title = place.name || '';
            el.innerHTML = placeMarkerHtml(place, isSelected, markerMode);

            el.addEventListener('click', (e) => {
                e.stopPropagation();
                setSelectedPlace(place);
                onPlaceSelect?.(place);
                map.setCenter(pos);
            });

            const overlay = new kakao.maps.CustomOverlay({
                position: pos,
                content: el,
                yAnchor: 0.5,
                xAnchor: 0.5,
                map,
            });

            overlaysRef.current.push(overlay);
        });

        return () => clearMarkers();
    }, [placesKey, selectedPlace?.id, sdkLoaded, kakaoMapEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

    /* 열 때 한 번 물어봅니다.
       측위는 SDK 가 들고 있습니다 — 네이티브 GET_CURRENT_LOCATION 이
       background.getCurrentPosition(maxAgeMs 5분, lowPower, 마지막 위치 폴백)
       을 부르고, useUserLocation 이 그 위에 캐시와 중복 요청 합치기를 얹습니다.
       그래서 캐시가 있으면 그대로 오고, 측위가 새로 돌지 않습니다. */
    useEffect(() => {
        let alive = true;
        void fetchUserLocation().then(loc => {
            if (alive && loc) setMyLoc({ lat: loc.lat, lng: loc.lng });
        });
        return () => { alive = false; };
        // fetchUserLocation 은 캐시가 바뀔 때마다 새로 만들어집니다 —
        // 의존성에 넣으면 열 때 한 번이 아니라 계속 돕니다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* 백그라운드 정밀 측위가 좌표를 올리면 점도 따라갑니다
       (useUserLocation 이 location-update 를 받아 atom 을 갱신합니다). */
    useEffect(() => {
        if (cachedUserLocation) {
            setMyLoc({ lat: cachedUserLocation.lat, lng: cachedUserLocation.lng });
        }
    }, [cachedUserLocation]);

    /* 내 위치 점 — 장소 마커와 따로 둡니다. 같이 두면 목록이 바뀔 때마다
       지워졌다 다시 그려져 깜빡입니다. */
    useEffect(() => {
        if (!useKakao || !sdkLoaded || !mapRef.current || !window.kakao?.maps) return;
        if (myLocOverlayRef.current) {
            myLocOverlayRef.current.setMap(null);
            myLocOverlayRef.current = null;
        }
        if (!myLoc) return;
        const el = document.createElement('div');
        el.innerHTML = myLocationHtml();
        el.style.pointerEvents = 'none';   // 점 때문에 지도를 못 누르면 안 됩니다
        myLocOverlayRef.current = new kakao.maps.CustomOverlay({
            position: new kakao.maps.LatLng(myLoc.lat, myLoc.lng),
            content: el,
            yAnchor: 0.5,
            xAnchor: 0.5,
            zIndex: 1,                     // 장소 마커보다 아래
            map: mapRef.current,
        });
        return () => {
            if (myLocOverlayRef.current) {
                myLocOverlayRef.current.setMap(null);
                myLocOverlayRef.current = null;
            }
        };
    }, [myLoc, useKakao, sdkLoaded, kakaoMapEpoch]);

    /* 고른 장소를 팝업 위로 올립니다.

       밀어야 할 거리는 "팝업이 가리는 화면 픽셀" 입니다. 예전에는 그걸 위경도로
       어림했는데(2^(level-1) * 0.0006 도), 줌 레벨에 지수로 물려 있어 축소할수록
       폭주했습니다 — 레벨 8 · 팝업 열림이면 8.5km 라, 누른 자리가 화면 밖으로
       밀려나 "고른 곳이 아니라 다른 데가 보인다" 가 됐습니다.

       지도의 투영으로 픽셀만큼만 옮깁니다. 줌과 무관하게 정확합니다. */
    useEffect(() => {
        if (!useKakao || !mapRef.current || !selectedPlace) return;
        const map = mapRef.current as any;
        const at = new kakao.maps.LatLng(selectedPlace.lat, selectedPlace.lng);

        /* 팝업이 열려 있으면 화면 높이의 5분의 1쯤, 아니면 카드 높이만큼만.
           지도 높이를 못 읽으면 흔한 화면 기준으로 둡니다. */
        const boxH = (typeof map.getNode === 'function' && map.getNode()?.clientHeight) || 600;
        const shiftPx = detailOpenProp ? Math.round(boxH * 0.2) : 60;

        const move = (target: any) => {
            if (typeof map.panTo === 'function') map.panTo(target);
            else map.setCenter(target);
        };

        const proj = typeof map.getProjection === 'function' ? map.getProjection() : null;
        if (proj && typeof proj.pointFromCoords === 'function'
            && typeof proj.coordsFromPoint === 'function') {
            const pt = proj.pointFromCoords(at);
            // 중심을 아래로 내리면 장소가 위로 올라옵니다
            pt.y += shiftPx;
            move(proj.coordsFromPoint(pt));
            return;
        }
        // 투영을 못 쓰면 옮기지 않고 그 자리에 둡니다 — 어림해서 엉뚱한 데로 가느니 낫습니다
        move(at);
    }, [selectedPlace, useKakao, detailOpenProp]);

    useEffect(() => {
        if (!center) return;
        // 지도를 옮기는 것은 카카오만 여기서 합니다 (구글은 자기 center prop 으로 따라갑니다)
        if (useKakao && mapRef.current) {
            mapRef.current.setCenter(new kakao.maps.LatLng(center.lat, center.lng));
        }
        /* '여기서 검색' 기준점은 두 지도 모두 새로 맞추고 버튼을 숨깁니다.
           해외에서 이걸 빼면 검색 결과를 고른 직후 기준점이 옛 자리에 남아
           idle 이 곧바로 200m 초과로 보고 버튼이 헛되게 떠 있었습니다. */
        lastQueryCenterRef.current = center;
        setSearchHerePending(null);
    }, [center, useKakao, sdkLoaded]);

    // 선택된 카드로 자동 스크롤
    useEffect(() => {
        if (!selectedPlace) return;
        const cardEl = cardRefs.current.get(selectedPlace.id);
        if (cardEl && cardScrollRef.current) {
            cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [selectedPlace]);

    // 카드 스와이프 시 보이는 장소 ID 알림 → 부모에서 사진 요청
    useEffect(() => {
        const container = cardScrollRef.current;
        if (!container || !onVisiblePlacesChange || displayPlaces.length === 0) return;

        let debounceTimer: ReturnType<typeof setTimeout>;

        const handleScroll = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const containerRect = container.getBoundingClientRect();
                const visibleIds: string[] = [];

                cardRefs.current.forEach((el, id) => {
                    const rect = el.getBoundingClientRect();
                    if (rect.right > containerRect.left && rect.left < containerRect.right) {
                        visibleIds.push(id);
                    }
                });

                if (visibleIds.length > 0) {
                    onVisiblePlacesChange(visibleIds);
                }
            }, 200);
        };

        container.addEventListener('scroll', handleScroll);
        // 초기 로드 시에도 한 번 호출
        handleScroll();

        return () => {
            clearTimeout(debounceTimer);
            container.removeEventListener('scroll', handleScroll);
        };
    }, [displayPlaces, onVisiblePlacesChange]);

    // Leaflet 지도 클릭 핸들러
    const handleLeafletMapClick = useCallback((lat: number, lng: number) => {
        const newCustomPlace: MapPlace = {
            id: `custom-${Date.now()}`,
            name: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
            address: i18n.t('placeMap.customLocation'),
            lat, lng,
            categoryIcon: 'pushpin',
            categoryLabel: i18n.t('placeMap.directSelect'),
        };
        setCustomPlace(newCustomPlace);
        setSelectedPlace(newCustomPlace);
        onPlaceSelect?.(newCustomPlace);
    }, [onPlaceSelect]);

    return (
        <div className={cls("place-map-view")} style={{ height }}>
            {/* 국내는 카카오, 해외는 Leaflet+OSM.
                앱에서는 해외 장소 고르기가 네이티브 화면으로 넘어가므로 여기까지 오지 않습니다 —
                브라우저에서만 이 지도를 씁니다. 웹뷰 안에서 구글 지도는 쓰지 않습니다(로드당 과금). */}
            {useKakao ? (
                <div className={cls("pmv-map")} ref={mapContainerRef} />
            ) : (
                <div className={cls("pmv-map")}>
                    <LeafletPlacePicker
                        places={displayPlaces}
                        center={center || { lat: 37.5665, lng: 126.978 }}
                        selectedPlaceId={selectedPlace?.id}
                        onPlaceSelect={(p) => { setSelectedPlace(p); onPlaceSelect?.(p); }}
                        onMapClick={handleLeafletMapClick}
                        onIdleCenter={handleIdleCenter}
                        detailOpen={detailOpenProp}
                        markerMode={markerMode}
                        myLocation={myLoc}
                    />
                </div>
            )}

            {/* 드롭다운 열려있을 때 지도를 눌러 닫기 — 지도 바로 위, 나머지 UI 아래.
               검색 wrapper 안에 두면 안 됩니다. wrapper(z-index 6)가 쌓임 맥락을 만들어
               그 안에서는 backdrop(z-index 5)이 z-index 없는 드롭다운보다 나중에 칠해지고,
               inset:0 이 드롭다운까지 포함한 wrapper 전체를 덮습니다. 그래서 결과를 눌러도
               backdrop 이 클릭을 가로채 드롭다운만 닫히고 아무 일도 일어나지 않았습니다 —
               인천공항을 골라도 지도가 인덕원에 머물던 원인입니다.
               자리는 지도 직후입니다. 같은 z-index 5 끼리는 DOM 순서로 칠해지므로,
               뒤에 오는 '여기서 검색'·즐겨찾는 지역·카드·버튼은 그대로 눌립니다.
               조건은 dropdown 컨테이너와 같게 — 둘 다 비면 backdrop 만 남아 지도 panning 이 막힙니다. */}
            {showSearch && showSearchDropdown && (searchQuery.trim() || historyMatches.length > 0) && (
                <div className={cls("pmv-search-backdrop")} onClick={() => setShowSearchDropdown(false)} />
            )}

            {/* '여기서 검색' 떠다니는 버튼 — 국내·해외 공용. 지도 패닝 시 화면 중심이 200m 이상 멀어지면 노출 */}
            {searchHerePending && (
                <button
                    className={cls("pmv-search-here")}
                    onClick={() => {
                        if (!searchHerePending) return;
                        lastQueryCenterRef.current = searchHerePending;
                        onCenterChangeRef.current?.(searchHerePending);
                        setSearchHerePending(null);
                    }}
                    aria-label={i18n.t('placeMap.researchArea')}
                >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <polyline points="23 4 23 10 17 10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                    <span>{i18n.t('placeMap.researchArea')}</span>
                </button>
            )}

            {/* 즐겨찾는 지역 칩 (가로 스크롤) — provider 무관 노출 (Google/Kakao 모두 center 갱신 방식으로 이동) */}
            {(favorites.length > 0 || editFavMode) && (
                <div className={cls("pmv-fav-row")} aria-label={i18n.t('placeMap.favRegions')}>
                    {favorites.map(fav => (
                        <div key={fav.name} className={cls("pmv-fav-chip-wrap")}>
                            <button
                                type="button"
                                className={cls("pmv-fav-chip")}
                                onClick={() => handleFavoriteClick(fav)}
                                title={i18n.t('placeMap.moveTo', { name: fav.name })}
                            >
                                <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden>
                                    <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                </svg>
                                <span className={cls("pmv-fav-name")}>{fav.name}</span>
                            </button>
                            {editFavMode && (
                                <button
                                    type="button"
                                    className={cls("pmv-fav-remove")}
                                    onClick={() => handleRemoveFavorite(fav.name)}
                                    aria-label={i18n.t('placeMap.deleteFav', { name: fav.name })}
                                >×</button>
                            )}
                        </div>
                    ))}
                    <button
                        type="button"
                        className={cls("pmv-fav-add")}
                        onClick={() => { setFavNameInput(''); setShowAddFav(true); }}
                        aria-label={i18n.t('placeMap.addCurrentFav')}
                    >＋</button>
                    {favorites.length > 0 && (
                        <button
                            type="button"
                            className={cls(`pmv-fav-edit ${editFavMode ? 'on' : ''}`)}
                            onClick={() => setEditFavMode(v => !v)}
                            aria-label={i18n.t('placeMap.edit')}
                        >{editFavMode ? i18n.t('placeMap.done') : i18n.t('placeMap.edit')}</button>
                    )}
                </div>
            )}

            {/* 즐겨찾기 추가 모달 */}
            {showAddFav && (
                <div className={cls("pmv-fav-modal")} onClick={() => setShowAddFav(false)}>
                    <div className={cls("pmv-fav-modal-card")} onClick={e => e.stopPropagation()}>
                        <div className={cls("pmv-fav-modal-title")}>{i18n.t('placeMap.favName')}</div>
                        <div className={cls("pmv-fav-modal-sub")}>{i18n.t('placeMap.favNameSub')}</div>
                        <input
                            className={cls("pmv-fav-modal-input")}
                            placeholder={i18n.t('placeMap.favPlaceholder')}
                            value={favNameInput}
                            autoFocus
                            onChange={e => setFavNameInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleAddFavorite(); }}
                            maxLength={20}
                        />
                        <div className={cls("pmv-fav-modal-actions")}>
                            <button className={cls("pmv-fav-modal-cancel")} onClick={() => setShowAddFav(false)}>{i18n.t('common.cancel')}</button>
                            <button className={cls("pmv-fav-modal-save")} onClick={handleAddFavorite}>{i18n.t('mapUi.save')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 검색바 + 드롭다운 */}
            {showSearch && (
                <div className={cls("pmv-search-wrapper")}>
                    <div className={cls("pmv-search-bar")}>
                        <input
                            ref={searchInputRef}
                            type="text"
                            className={cls("pmv-search-input")}
                            placeholder={i18n.t('placeMap.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => {
                                const v = e.target.value;
                                setSearchQuery(v);
                                setHistoryMatches(matchPlaceHistory(v, 5));
                                if (!v.trim()) {
                                    setSearchResults([]);
                                }
                                // setShowSearchDropdown 는 useEffect[searchQuery] 가 일관되게 set
                            }}
                            onFocus={async () => {
                                /* 해외 + 네이티브앱 → 네이티브 구글 장소검색.
                                   openNativePlaceSearch 는 한국 전용 피커를 엽니다
                                   (네이티브가 components=country:kr 로 검색해서 해외에서는
                                   결과가 하나도 안 나옵니다). 국가 제한이 없는 쪽을 씁니다. */
                                if (!useKakao && isNativeApp()) {
                                    searchInputRef.current?.blur();
                                    const result = await openNativeGooglePlaceSearch({
                                        initialLocation: center,
                                        initialQuery: searchQuery || undefined,
                                        title: nativeSearchTitle,
                                    });
                                    if (result) {
                                        /* 예전에는 여기서 상태만 바꾸고 지도를 안 움직였습니다.
                                           부모에게도 안 알려서 주변 재조회가 안 돌고, 마커는
                                           화면 밖에 찍혀 「골랐는데 아무 일도 안 생김」 으로 보였습니다.
                                           결과 선택 뒤처리는 한 곳에서만 합니다. */
                                        handleSearchItemSelect({
                                            id: `native-${Date.now()}`,
                                            name: result.name,
                                            address: result.address,
                                            lat: result.lat,
                                            lng: result.lng,
                                            categoryIcon: 'pin',
                                            isSearchResult: true,
                                        });
                                    }
                                    return;
                                }
                                // focus 시 검색 기록(빈 입력이면 최근 5개) 표시
                                setHistoryMatches(matchPlaceHistory(searchQuery, 5));
                                if (searchDropdownResults.length > 0 || matchPlaceHistory(searchQuery, 5).length > 0) {
                                    setShowSearchDropdown(true);
                                }
                            }}
                            readOnly={!useKakao && isNativeApp()}
                        />
                        {searchQuery && (
                            <button className={cls("pmv-search-clear")} onClick={() => {
                                setSearchQuery('');
                                setSearchResults([]);
                                setSearchDropdownResults([]);
                                setShowSearchDropdown(false);
                            }}><IcClose size={14} /></button>
                        )}
                    </div>

                    {/* 검색 결과 드롭다운 — 검색 기록(history) + API 결과 결합 */}
                    {showSearchDropdown && (searchQuery.trim() || historyMatches.length > 0) && (
                        <div className={cls("pmv-search-dropdown")}>
                            {/* 1) 검색 기록 — 입력어와 유사한 history (최대 5개) */}
                            {historyMatches.length > 0 && (
                                <>
                                    <div className={cls("pmv-search-dropdown-section")}>
                                        {searchQuery.trim() ? i18n.t('placeMap.relatedHistory') : i18n.t('placeMap.recentSearch')}
                                    </div>
                                    {historyMatches.map((h, idx) => (
                                        <button
                                            key={`hist-${idx}-${h.name}`}
                                            className={cls("pmv-search-dropdown-item")}
                                            onClick={() => {
                                                /* 좌표가 없는 기록은 (0,0) 이 돼 지도가 대서양으로
                                                   넘어가고 마커도 안 찍힙니다. 그때는 이름만 넣어
                                                   다시 검색하게 둡니다. */
                                                if (h.lat == null || h.lng == null) {
                                                    setSearchQuery(h.name);
                                                    return;
                                                }
                                                handleSearchItemSelect({
                                                    id: `hist-${h.lat},${h.lng}`,
                                                    name: h.name,
                                                    address: h.address || '',
                                                    lat: h.lat,
                                                    lng: h.lng,
                                                    isSearchResult: true,
                                                });
                                            }}
                                        >
                                            <span className={cls("pmv-dropdown-icon")} aria-hidden>
                                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="9" />
                                                    <polyline points="12 7 12 12 15 14" />
                                                </svg>
                                            </span>
                                            <div className={cls("pmv-dropdown-info")}>
                                                <div className={cls("pmv-dropdown-name")}>{h.name}</div>
                                                <div className={cls("pmv-dropdown-addr")}>{h.address || ''}</div>
                                            </div>
                                        </button>
                                    ))}
                                </>
                            )}

                            {/* 2) API 검색 결과 */}
                            {searchQuery.trim() && (
                                isSearching ? (
                                    <div className={cls("pmv-search-dropdown-loading")}>{i18n.t('placeMap.searching')}</div>
                                ) : searchDropdownResults.length > 0 ? (
                                    <>
                                        {historyMatches.length > 0 && (
                                            <div className={cls("pmv-search-dropdown-section")}>{i18n.t('placeMap.searchResults')}</div>
                                        )}
                                        {searchDropdownResults.map(place => {
                                            // 거리 라벨 — distanceReferenceCenter 가 있으면 항목별 거리 계산
                                            let distLabel: string | null = null;
                                            if (distanceReferenceCenter && typeof place.lat === 'number' && typeof place.lng === 'number') {
                                                const m = distanceMeters(distanceReferenceCenter, { lat: place.lat, lng: place.lng });
                                                distLabel = m < 1000 ? `${Math.round(m)}m` : `${(m / 1000).toFixed(1)}km`;
                                            }
                                            return (
                                                <button
                                                    key={place.id}
                                                    className={cls("pmv-search-dropdown-item")}
                                                    onClick={() => handleSearchItemSelect(place)}
                                                >
                                                    <span className={cls("pmv-dropdown-icon")}><IcPin size={16} /></span>
                                                    <div className={cls("pmv-dropdown-info")}>
                                                        <div className={cls("pmv-dropdown-name")}>{place.name}</div>
                                                        <div className={cls("pmv-dropdown-addr")}>
                                                            {place.category && <span className={cls("pmv-dropdown-category")}>{place.category}</span>}
                                                            {place.address}
                                                        </div>
                                                        {distLabel && (
                                                            <div className={cls("pmv-dropdown-distance")}>
                                                                {distanceReferenceLabel} {distLabel}
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </>
                                ) : historyMatches.length === 0 ? (
                                    <div className={cls("pmv-search-dropdown-empty")}>{i18n.t('placeMap.noResults')}</div>
                                ) : null
                            )}
                        </div>
                    )}

                </div>
            )}


            {/* 카테고리 탭 */}
            {showCategories && categories && (
                <div className={cls("pmv-category-bar")} style={showSearch ? { top: '58px' } : undefined}>
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            className={cls(`pmv-category-tab ${selectedCategory === cat.id ? 'active' : ''}`)}
                            onClick={() => onCategoryChange?.(cat.id)}
                        >
                            {/* 키 문자열을 그대로 찍어 'attraction 전체' 처럼 보였습니다 */}
                            <CategoryIcon icon={cat.icon} size={14} />
                            <span>{cat.label}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* 상단 버튼 */}
            <div className={cls("pmv-top-actions")} style={showSearch && showCategories ? { top: '98px' } : showSearch ? { top: '58px' } : undefined}>
                {showBackButton && onClose && (
                    <button className={cls("pmv-action-btn")} onClick={onClose}>{i18n.t('placeMap.viewList')}</button>
                )}
            </div>

            {/* 현재 위치 버튼 — floating, 우측 하단 */}
            <button
                type="button"
                className={cls("pmv-my-location-btn")}
                onClick={async () => {
                    if (isFetchingMyLoc) return;
                    setIsFetchingMyLoc(true);
                    try {
                        const loc = await fetchUserLocation(true);
                        if (loc) {
                            const next = { lat: loc.lat, lng: loc.lng };
                            setMyLoc(next);
                            if (mapRef.current && window.kakao?.maps) {
                                mapRef.current.setCenter(new window.kakao.maps.LatLng(next.lat, next.lng));
                            }
                            onCenterChangeRef.current?.(next);
                        }
                    } finally {
                        setIsFetchingMyLoc(false);
                    }
                }}
                aria-label={i18n.t('mapUi.currentLocation')}
                title={i18n.t('mapUi.currentLocation')}
                disabled={isFetchingMyLoc}
            >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="12" cy="12" r="3" />
                    <line x1="12" y1="2" x2="12" y2="5" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                    <line x1="2" y1="12" x2="5" y2="12" />
                    <line x1="19" y1="12" x2="22" y2="12" />
                </svg>
            </button>

            {/* 하단 카드 + 확인 */}
            <div className={cls("pmv-bottom-cards")}>
                {displayPlaces.length > 0 && (
                    <div className={cls("pmv-card-scroll")} ref={cardScrollRef}>
                        {displayPlaces.map(place => {
                            const isActive = selectedPlace?.id === place.id;
                            return (
                                <button
                                    key={place.id}
                                    ref={(el) => { if (el) cardRefs.current.set(place.id, el); }}
                                    className={cls(`pmv-place-card ${isActive ? 'active' : ''} ${place.isSearchResult ? 'search-result' : ''} ${place.id.startsWith('custom-') ? 'custom-place' : ''}`)}
                                    onClick={() => {
                                        setSelectedPlace(place);
                                        onPlaceSelect?.(place);
                                    }}
                                >
                                    {place.imageUrl ? (
                                        <img src={place.imageUrl} alt={place.name} className={cls("pmv-card-img")} />
                                    ) : (
                                        <div className={cls("pmv-card-img-empty")}><CategoryIcon icon={place.categoryIcon} size={20} /></div>
                                    )}
                                    <div className={cls("pmv-card-body")}>
                                        {place.categoryLabel === '직접 선택' && (
                                            <div className={cls("pmv-card-custom-badge")}><IcPushpin size={13} /> {i18n.t('placeMap.customLocation')}</div>
                                        )}
                                        <div className={cls("pmv-card-name")}>{place.name}</div>
                                        {place.rating && (
                                            <div className={cls("pmv-card-rating")}><IcStar size={13} /> {place.rating}{place.reviewCount ? ` (${place.reviewCount})` : ''}</div>
                                        )}
                                        <div className={cls("pmv-card-addr")}>{place.address}</div>
                                        {place.distance && (
                                            <div className={cls("pmv-card-distance")}>
                                                {distanceReferenceLabel} {place.distance}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}

                {selectedPlace && onPlaceConfirm && (
                    <div className={cls("pmv-confirm-bar")}>
                        <button className={cls("pmv-confirm-btn")} onClick={() => onPlaceConfirm(selectedPlace)}>
                            {confirmButtonText}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
